/**
 * Utility functions for handling and formatting errors
 */

/**
 * Parse Firebase authentication error messages into user-friendly format
 * @param error - The error object or message from Firebase
 * @returns A user-friendly error message
 */
export const formatFirebaseAuthError = (error: any): string => {
    // If the error is already a string, return it
    if (typeof error === 'string') return error;

    // Handle null or undefined
    if (!error) return 'auth.errors.generic';

    // Get the error code if available
    const errorCode = error.code || '';

    // Map Firebase auth error codes to user-friendly messages
    switch (errorCode) {
        // Email errors
        case 'auth/email-already-in-use':
            return 'auth.errors.emailAlreadyInUse';
        case 'auth/invalid-email':
            return 'auth.errors.invalidEmail';
        case 'auth/user-not-found':
            return 'auth.errors.userNotFound';

        // Password errors
        case 'auth/wrong-password':
            return 'auth.errors.wrongPassword';
        case 'auth/weak-password':
            return 'auth.errors.weakPassword';

        // Login errors
        case 'auth/invalid-credential':
            return 'auth.errors.invalidCredentials';
        case 'auth/invalid-login-credentials':
            return 'auth.errors.invalidCredentials';

        // Account status errors
        case 'auth/user-disabled':
            return 'auth.errors.userDisabled';
        case 'auth/account-exists-with-different-credential':
            return 'auth.errors.credentialAlreadyInUse'; // Assuming this is the closest fit

        // Network errors
        case 'auth/network-request-failed':
            return 'auth.errors.networkError'; // Use existing networkError key

        // Too many requests
        case 'auth/too-many-requests':
            // Check if the error message contains keywords related to verification
            if (
                error.message &&
                (error.message.toLowerCase().includes('verification') || error.message.toLowerCase().includes('verify'))
            ) {
                return 'auth.errors.tooManyVerificationRequests';
            }
            return 'auth.errors.tooManyRequests';

        // Timeout error
        case 'auth/timeout':
            return 'auth.errors.timeout';

        // Verification errors
        case 'auth/missing-verification-code':
        case 'auth/invalid-verification-code':
            return 'auth.errors.invalidVerificationCode';

        // Email verification
        case 'auth/email-verification-required':
            return 'auth.errors.emailNotVerified'; // Assuming this fits

        // User token error
        case 'auth/user-token-expired':
            return 'auth.errors.generic'; // No specific key, use generic
        case 'auth/invalid-user-token':
            return 'auth.errors.generic'; // No specific key, use generic

        // Reset password
        case 'auth/expired-action-code':
            return 'auth.errors.expiredActionCode';
        case 'auth/invalid-action-code':
            return 'auth.errors.invalidActionCode';

        // General errors
        case 'auth/operation-not-allowed':
            return 'auth.errors.operationNotAllowed';
        case 'auth/popup-closed-by-user':
            return 'auth.errors.popupClosedByUser';

        // CORS errors
        case 'auth/cors-unsupported':
            return 'auth.errors.generic'; // No specific key, use generic

        // Default case
        default:
            // Attempt to return a cleaned message if available, otherwise generic error
            if (typeof error.message === 'string') {
                const message = error.message
                    .replace('Firebase: ', '')
                    .replace(/\(auth\/.*\)\.?/, '') // Remove (auth/code) part
                    .trim();
                console.log('Formatted message:', message);
            }
            return 'auth.errors.generic';
    }
};
